/**
 * Shared Ticket Operations Hooks
 *
 * Consolidated hooks for ticket operations used across admin features.
 * This replaces duplicate implementations in useAdminOrderMutations and useTicketMutations.
 */

import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { safeShowAlert } from "@/lib/telegram";
import { getErrorMessage } from "@/lib/graphql";

// =============================================================================
// TYPES
// =============================================================================

export interface PaymentActionResult {
  success: boolean;
  message: string | null;
}

interface ResendTicketsOptions {
  customMessage?: string;
}

interface RegenerateTicketsOptions {
  imageOnly?: boolean;
  sendToUser?: boolean;
  customMessage?: string;
}

export interface CashTicketItem {
  ticketTypeId: string;
  quantity: number;
}

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const GenerateTicketsMutation = `
  mutation GenerateTickets($orderId: ID!) {
    generateTickets(orderId: $orderId) {
      success
      message
    }
  }
`;

const ResendTicketsMutation = `
  mutation ResendTickets($orderId: ID!, $options: ResendTicketsInput) {
    resendTickets(orderId: $orderId, options: $options) {
      success
      message
    }
  }
`;

const ResendTicketMutation = `
  mutation ResendTicket($ticketId: ID!, $options: ResendTicketsInput) {
    resendTicket(ticketId: $ticketId, options: $options) {
      success
      message
    }
  }
`;

const RegenerateTicketsMutation = `
  mutation RegenerateTickets($orderId: ID!, $options: RegenerateTicketsInput) {
    regenerateTickets(orderId: $orderId, options: $options) {
      success
      message
    }
  }
`;

const RegenerateTicketMutation = `
  mutation RegenerateTicket($ticketId: ID!, $options: RegenerateTicketsInput) {
    regenerateTicket(ticketId: $ticketId, options: $options) {
      success
      message
    }
  }
`;

const IssueTicketsForCashMutation = `
  mutation IssueTicketsForCash($accountId: ID!, $eventId: ID!, $items: [CashTicketItemInput!]!) {
    issueTicketsForCash(accountId: $accountId, eventId: $eventId, items: $items) {
      success
      message
    }
  }
`;

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook for generating tickets for an order.
 * Use when tickets need to be created after payment approval.
 */
export function useGenerateTickets() {
  const [result, executeMutation] = useMutation<{
    generateTickets: PaymentActionResult;
  }>(GenerateTicketsMutation);

  const generateTickets = async (orderId: string): Promise<PaymentActionResult | null> => {
    errorTracking.addBreadcrumb({
      category: "admin.tickets",
      message: `Generating tickets for order ${orderId}`,
      level: "info",
    });

    const response = await executeMutation({ orderId });

    if (response.error) {
      errorTracking.captureError(response.error, {
        tags: { operation: "generateTickets" },
      });
      safeShowAlert(getErrorMessage(response.error, "Произошла ошибка"));
      return null;
    }

    return response.data?.generateTickets ?? null;
  };

  return {
    generateTickets,
    isLoading: result.fetching,
  };
}

/**
 * Hook for resending tickets to a user by order ID.
 * Use to resend all tickets for an order.
 */
export function useResendTickets() {
  const [result, executeMutation] = useMutation<{
    resendTickets: PaymentActionResult;
  }>(ResendTicketsMutation);

  const resendTickets = async (
    orderId: string,
    options?: ResendTicketsOptions
  ): Promise<PaymentActionResult | null> => {
    errorTracking.addBreadcrumb({
      category: "admin.tickets",
      message: `Resending tickets for order ${orderId}`,
      level: "info",
    });

    const response = await executeMutation({ orderId, options });

    if (response.error) {
      errorTracking.captureError(response.error, {
        tags: { operation: "resendTickets" },
      });
      safeShowAlert(getErrorMessage(response.error, "Произошла ошибка"));
      return null;
    }

    return response.data?.resendTickets ?? null;
  };

  return {
    resendTickets,
    isLoading: result.fetching,
  };
}

/**
 * Hook for resending a single ticket.
 */
export function useResendTicket() {
  const [result, executeMutation] = useMutation<{
    resendTicket: PaymentActionResult;
  }>(ResendTicketMutation);

  const resendTicket = async (
    ticketId: string,
    options?: ResendTicketsOptions
  ): Promise<PaymentActionResult | null> => {
    errorTracking.addBreadcrumb({
      category: "admin.tickets",
      message: `Resending ticket ${ticketId}`,
      level: "info",
    });

    const response = await executeMutation({ ticketId, options });

    if (response.error) {
      errorTracking.captureError(response.error, {
        tags: { operation: "resendTicket" },
      });
      safeShowAlert(getErrorMessage(response.error, "Произошла ошибка"));
      return null;
    }

    return response.data?.resendTicket ?? null;
  };

  return {
    resendTicket,
    isLoading: result.fetching,
  };
}

/**
 * Hook for regenerating tickets by order ID.
 * Use to regenerate images or resend after regeneration.
 */
export function useRegenerateTickets() {
  const [result, executeMutation] = useMutation<{
    regenerateTickets: PaymentActionResult;
  }>(RegenerateTicketsMutation);

  const regenerateTickets = async (
    orderId: string,
    options?: RegenerateTicketsOptions
  ): Promise<PaymentActionResult | null> => {
    errorTracking.addBreadcrumb({
      category: "admin.tickets",
      message: `Regenerating tickets for order ${orderId}`,
      level: "info",
    });

    const response = await executeMutation({ orderId, options });

    if (response.error) {
      errorTracking.captureError(response.error, {
        tags: { operation: "regenerateTickets" },
      });
      safeShowAlert(getErrorMessage(response.error, "Произошла ошибка"));
      return null;
    }

    return response.data?.regenerateTickets ?? null;
  };

  return {
    regenerateTickets,
    isLoading: result.fetching,
  };
}

/**
 * Hook for regenerating a single ticket.
 */
export function useRegenerateTicket() {
  const [result, executeMutation] = useMutation<{
    regenerateTicket: PaymentActionResult;
  }>(RegenerateTicketMutation);

  const regenerateTicket = async (
    ticketId: string,
    options?: RegenerateTicketsOptions
  ): Promise<PaymentActionResult | null> => {
    errorTracking.addBreadcrumb({
      category: "admin.tickets",
      message: `Regenerating ticket ${ticketId}`,
      level: "info",
    });

    const response = await executeMutation({ ticketId, options });

    if (response.error) {
      errorTracking.captureError(response.error, {
        tags: { operation: "regenerateTicket" },
      });
      safeShowAlert(getErrorMessage(response.error, "Произошла ошибка"));
      return null;
    }

    return response.data?.regenerateTicket ?? null;
  };

  return {
    regenerateTicket,
    isLoading: result.fetching,
  };
}

/**
 * Hook for issuing tickets for cash payment.
 * Use in AdminCashTicketsPage for on-site ticket issuance.
 */
export function useIssueTicketsForCash() {
  const [result, executeMutation] = useMutation<{
    issueTicketsForCash: PaymentActionResult;
  }>(IssueTicketsForCashMutation);

  const issueTicketsForCash = async (
    accountId: string,
    eventId: string,
    items: CashTicketItem[]
  ): Promise<PaymentActionResult | null> => {
    errorTracking.addBreadcrumb({
      category: "admin.tickets",
      message: `Issuing tickets for cash: event ${eventId}, account ${accountId}`,
      level: "info",
    });

    const response = await executeMutation({ accountId, eventId, items });

    if (response.error) {
      errorTracking.captureError(response.error, {
        tags: { operation: "issueTicketsForCash" },
      });
      safeShowAlert(getErrorMessage(response.error, "Произошла ошибка"));
      return null;
    }

    return response.data?.issueTicketsForCash ?? null;
  };

  return {
    issueTicketsForCash,
    isLoading: result.fetching,
  };
}
